import { ConfirmationActions, SearchMeditatorActions, TableTitles } from "@/constants"
import { useConfirmRejectCancellationRequest, useGetCancellationRequestedMeditators } from "@/hooks/query-hooks/useMeditators"
import { cn } from "@/libs/cn"
import { Button, Divider, Space, TableProps } from "antd"
import { useState } from "react"
import { Helmet } from 'react-helmet-async'
import { AnimatedComponent, PageTitle } from "../common"
import { ManualRegisterMeditator } from "./ManualRegisterMeditator"
import { RegistrantDetailDrawer } from "./RegistrantDetailDrawer"
import { RegistrantTable } from "./RegistrantTable"
import { RejectRegistrantModal } from "./RejectRegistrantModal"

export function CancellationRequestRegistrants() {

    const [page, setPage] = useState(1);
    const { data, isLoading } = useGetCancellationRequestedMeditators(page);
    const meditators = data?.content || [];

    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);

    const renderColumnsKey = [
        TableTitles.registrant_name,
        TableTitles.retreat_level,
        TableTitles.retreat_name,
        TableTitles.retreat_start_date,
        TableTitles.retreat_end_date,
        TableTitles.total_registrants,
        TableTitles.action
    ];

    const rowSelection: TableProps<MeditatorWithMeditationRetreat>['rowSelection'] = {
        type: "checkbox",
        selectedRowKeys,
        onChange: (selectedRowKeys: React.Key[]) => {
            setSelectedRowKeys(selectedRowKeys);
        },
    };

    const { mutate, isLoading: isMutating } = useConfirmRejectCancellationRequest()
    const handleAcceptReject = (action: ConfirmationActions) => {

        const keyword = action === ConfirmationActions.ACCEPT ? "Accept" : "Reject";
        if (selectedRowKeys.length === 0) {
            alert(`Please select at least one registrant to ${keyword}.`);
            return;
        }
        const ids = selectedRowKeys.map((key) => Number(key));

        const payload = {
            ids,
            action
        };

        mutate(payload, {
            onSuccess: () => {
                setSelectedRowKeys([]);
                setPage(1); // Reset to the first page after action
            }
        })
    }

    return (
        <AnimatedComponent>
            <Helmet>
                <title>စာရင်းပယ်ဖျက် ယောဂီများ</title>
            </Helmet>
            <div
                className="flex items-center justify-between gap-4"
            >
                <PageTitle>
                    စာရင်းပယ်ဖျက်ရန် ဖောင်တင်သွင်းထားသော ယောဂီများ
                </PageTitle>
                <Space
                    className="flex-1 justify-end relative"
                >
                    <Button
                        size="large"
                        type="link"
                        onClick={() => handleAcceptReject(ConfirmationActions.ACCEPT)}
                        className={cn("!font-poppins duration-300", {
                            "opacity-0 pointer-events-none": selectedRowKeys.length === 0,
                        })}
                        loading={isMutating}
                    >
                        Accept
                    </Button>
                    <Button
                        size="large"
                        type="text"
                        danger
                        onClick={() => handleAcceptReject(ConfirmationActions.REJECT)}
                        className={cn("!font-poppins duration-300", {
                            "opacity-0 pointer-events-none": selectedRowKeys.length === 0,
                        })}
                        loading={isMutating}
                    >
                        Reject
                    </Button>
                    <div
                        className={
                            cn("duration-500 absolute top-0 right-0", {
                                "-translate-x-[115%]": selectedRowKeys.length > 0,
                            })
                        }
                    >
                        <ManualRegisterMeditator
                            modalType={SearchMeditatorActions.CANCELLATION_REQUEST}
                        />
                    </div>
                </Space>

            </div>
            <Divider />
            <RegistrantTable
                loading={isLoading}
                dataSource={meditators}
                rowSelection={rowSelection}
                renderColumnKeys={renderColumnsKey}
                TableActionComponent={TableActionComponent}
                pagination={{
                    current: page,
                    onChange: (page) => setPage(page),
                    total: data?.totalElements || 0,
                }}
                rowKey={(record) => record.user.id.toString()}
            />
        </AnimatedComponent>
    )
}

const TableActionComponent = ({ record }: { record: MeditatorWithMeditationRetreat }) => {

    return (
        <>
            <RegistrantDetailDrawer
                registrant={record}
                confirmationAction={ConfirmationActions.ACCEPT}
                pathnameTitle="စာရင်းပယ်ဖျက်ရန် ဖောင်တင်သွင်းထားသော ယောဂီများ"
                drawerType={SearchMeditatorActions.CANCELLATION_REQUEST}
                Component={({ registrant, onToggleDrawer }) => (
                    <RejectRegistrantModal
                        registrantId={registrant.id}
                        onToggleDrawer={onToggleDrawer}
                        modalType="cancellation_pending"
                    />
                )}
            />
        </>
    )
}
